/**
 * API Configuration - Central Configuration for Environment Detection
 * Tránh hardcode URLs và tự động phát hiện môi trường development vs production
 */

// Environment detection utility  
export const getEnvironmentType = () => {
  const hostname = window.location.hostname;
  const port = window.location.port;
  
  // Production environment indicators
  const isProduction = process.env.REACT_APP_ENVIRONMENT === 'production' ||
                      process.env.NODE_ENV === 'production' ||
                      (!hostname.includes('localhost') && !hostname.includes('127.0.0.1'));
  
  const isDevelopment = !isProduction;
  
  return {
    isDevelopment,
    isProduction,
    hostname,
    protocol: window.location.protocol,
    port,
    origin: window.location.origin
  };
};

// API Base URL Configuration
export const getAPIBaseURL = (): string => {
  const env = getEnvironmentType();
  // Priority 1: Environment variable (for explicit configuration)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Priority 2: Auto-detect development (only if explicitly localhost)
  if (env.isDevelopment && (env.hostname === 'localhost' || env.hostname === '127.0.0.1')) {
    return 'http://localhost:3001/api/v1';
  }
  
  // Priority 3: Production/Hosting - use relative path với /api/v1
  return '/api/v1'; // Relative path với /api/v1 - API calls sẽ sử dụng cùng domain/port
};

// Axios Base URL (without /api/v1 for axios.defaults.baseURL)
export const getAxiosBaseURL = (): string => {
  const env = getEnvironmentType();
  // Priority 1: Environment variable
  if (process.env.REACT_APP_API_URL) {
    // Remove /api/v1 từ cuối để axios có thể thêm vào trong các API calls
    const baseUrl = process.env.REACT_APP_API_URL.replace(/\/api\/v1$/, '');
    return baseUrl;
  }
  
  // Priority 2: Auto-detect development (only if explicitly localhost)
  if (env.isDevelopment && (env.hostname === 'localhost' || env.hostname === '127.0.0.1')) {
    return 'http://localhost:3001'; // Base URL without /api/v1
  }
  
  // Priority 3: Production/Hosting - use empty string để dùng same origin
  return ''; // Empty string để dùng same origin
};

// API Endpoints Configuration
export const API_CONFIG = {
  BASE_URL: getAPIBaseURL(),
  ENDPOINTS: {
    // Authentication
    AUTH: {
      LOGIN: '/auth/login',
      LOGOUT: '/auth/logout',
      ME: '/auth/me',
      CHANGE_PASSWORD: '/auth/change-password',
      VERIFY_TOKEN: '/auth/verify-token',
      REFRESH_TOKEN: '/auth/refresh-token'
    },
    
    // Dashboard
    DASHBOARD: {
      STATISTICS: '/dashboard/statistics',
      AVAILABLE_YEARS: '/dashboard/available-years',
      STUDENT_STATS: '/dashboard/student-stats'
    },
    
    // Users & Permissions
    USERS: {
      BASE: '/users',
      TEACHERS: '/users/teachers',
      STUDENTS: '/users/students'
    },
    
    PERMISSIONS: {
      MODULES: '/permissions/modules'
    },
    
    // Academic Management
    COURSES: '/courses',
    SUBJECTS: '/subjects',
    CLASSES: '/classes',
    LOPHOCPHAN: '/lophocphan',
    LOPHOCPHAN_SINHVIEN: '/lophocphan-sinhvien',
    
    // Grades
    GRADES: '/grades',
    
    // Exam Generation
    EXAM_GENERATION: ''
  }
};

// Utility function to build full API URL
export const buildAPIURL = (endpoint: string): string => {
  const baseURL = API_CONFIG.BASE_URL;
  
  // Nếu endpoint đã có http/https thì return nguyên
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    return endpoint;
  }
  
  // Nếu baseURL trống (production) thì chỉ return endpoint
  if (!baseURL) {
    return endpoint;
  }
  
  // Kết hợp baseURL với endpoint
  const url = baseURL + endpoint;
  return url;
};

// Storage keys
export const STORAGE_KEYS = {
  TOKEN: 'quanly_truonghoc_token',
  USER: 'quanly_truonghoc_user',
  ROLE_PERMISSIONS: 'quanly_truonghoc_permissions',
  USER_PERMISSIONS: 'quanly_truonghoc_user_permissions'
};

// Helper function for common fetch with auth
export const fetchWithAuth = async (endpoint: string, options: RequestInit = {}) => {
  const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
  
  // Xây dựng URL đúng: baseURL + endpoint (baseURL đã có /api/v1, endpoint chỉ có path)
  let url = endpoint;
  if (!endpoint.startsWith('http')) {
    const baseURL = API_CONFIG.BASE_URL;
    url = baseURL ? baseURL + endpoint : endpoint;
  }
  
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers
  };
  return fetch(url, {
    ...options,
    headers
  });
};

export default API_CONFIG;
